import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';

import { SeoService } from '../../../core/seo/seo.service';
import { breadcrumbSchemaFor, breadcrumbTrail } from '../../../core/seo/breadcrumb-trail';
import { webPageSchema } from '../../../core/seo/structured-data';
import { Breadcrumb } from '../../../shared/breadcrumb/breadcrumb';
import { LegalDocument, PRIVACY_POLICY } from '../legal-content';

/**
 * Renders one legal document, chosen by the route's `data.document`.
 *
 * One component for both rather than one each: the pages differ only in their prose, and duplicating
 * the SEO, breadcrumb and layout wiring twice is how the two drift apart — a canonical fixed on one
 * and forgotten on the other.
 *
 * The content is compiled in rather than fetched. These are the pages a reader is sent to when they
 * want to know what happens to their data; a page that renders empty because an API call failed
 * would be worse than one that cannot be edited without a deploy.
 */
@Component({
  selector: 'app-legal-page',
  imports: [Breadcrumb],
  templateUrl: './legal-page.html',
  styleUrl: './legal-page.scss',
})
export class LegalPage {
  private readonly route = inject(ActivatedRoute);
  private readonly seo = inject(SeoService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly document = signal<LegalDocument>(PRIVACY_POLICY);

  protected readonly crumbs = computed(() =>
    breadcrumbTrail(
      { label: 'Home', path: '/' },
      { label: this.document().title, path: this.document().path },
    ),
  );

  constructor() {
    // `data`, not a snapshot: both documents use this component, so navigating between them reuses
    // the instance and a snapshot read would leave the first document's text on screen.
    this.route.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      const doc = (data['document'] as LegalDocument | undefined) ?? PRIVACY_POLICY;
      this.document.set(doc);
      this.applySeo(doc);
    });
  }

  /** Paragraphs and lists share one array so their order is preserved; this narrows the two apart. */
  protected isList(block: string | { list: string[] }): block is { list: string[] } {
    return typeof block !== 'string';
  }

  protected asText(block: string | { list: string[] }): string {
    return typeof block === 'string' ? block : '';
  }

  private applySeo(doc: LegalDocument): void {
    const url = this.seo.absolute(doc.path);

    this.seo.apply({
      title: doc.metaTitle,
      description: doc.metaDescription,
      canonicalPath: doc.path,
    });

    this.seo.setJsonLd([
      webPageSchema({
        origin: this.seo.origin,
        url,
        name: doc.metaTitle,
        description: doc.metaDescription,
        // A policy page is judged partly on how current it is, and this is the only machine-readable
        // way to say so. It matches the date printed at the top, from the same constant.
        dateModified: doc.updatedIso,
      }),
      breadcrumbSchemaFor(this.crumbs(), (path) => this.seo.absolute(path)),
    ]);
  }
}
